import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { UserState } from "@/context/User/UserReducer";
import { updatePlatform } from "@/services/Platform.service";
import { Platform, UpdatePlatformParams } from "@/types/platform.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form";

const formSchema = z.object({
	platformid: z.number(),
	platformname: z.string(),
});

const EditPlatform: React.FC<{
	user: UserState | null;
	platform: Platform;
}> = ({ user, platform }) => {
	const [open, setOpen] = useState(false);

	const queryClient = useQueryClient();

	const editPlatform = useMutation({
		mutationKey: ["platform_update", platform.platformid],
		mutationFn: (data: UpdatePlatformParams) =>
			updatePlatform(user?.accessToken!, data),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["platform", platform.platformid.toString()],
				exact: true,
				refetchType: "all",
			});
			setOpen(false);
			form.reset();
			toast.success("Success", {
				description: `Platform ${variables.platformname} has been successfully created`,
			});
			editPlatform.reset();
		},
		onError: (err) => {
			toast.error("Error!", { description: "Error adding platform, try again" });
			console.log(err);
		},
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			platformid: platform.platformid,
			platformname: platform.platformname,
		},
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		editPlatform.mutate(data);
	};

	const disabled = () => {
		if (editPlatform.isPending) return true;
		if (editPlatform.isSuccess) return true;
		return false;
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="default">Edit Platform</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle className="text-left font-bold text-2xl">
						Edit Platform
					</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<FormField
							name="platformid"
							render={({ field }) => (
								<FormItem className="flex-1 mb-2">
									<FormLabel>Platform ID</FormLabel>
									<FormControl>
										<Input
											type="number"
											defaultValue={platform.platformid}
											onChange={(e) => field.onChange(e.target.valueAsNumber)}
											disabled
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							name="platformname"
							render={({ field }) => (
								<FormItem className="flex-1 mb-2">
									<FormLabel>Name of Platform</FormLabel>
									<FormControl>
										<Input type="text" {...field} disabled={disabled()} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							className="mt-4 w-full"
							disabled={editPlatform.isPending || editPlatform.isSuccess}
						>
							{editPlatform.isPending ? "Updating..." : "Edit Platform"}
						</Button>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default EditPlatform;
